import { useState, useEffect } from 'react';
import { X, Smartphone } from 'lucide-react';
import { useAuthStore } from '../../stores/auth-store';
import { getAccessToken } from '../../lib/auth';
import { isMobileBrowser, openAppWithAuth, getAppStoreUrl } from '../../utils/mobile-detection';
import { useLanguage } from '../../contexts/LanguageContext';

const DISMISS_KEY = 'rumio-open-in-app-dismissed';
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Banner shown to logged-in users on mobile browsers,
 * offering to transfer their session to the mobile app.
 */
export default function OpenInAppBanner() {
  const { t } = useLanguage();
  const { isAuthenticated } = useAuthStore();
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check if we should show the banner
    const checkVisibility = () => {
      // Only on mobile browsers
      const mobile = isMobileBrowser();
      setIsMobile(mobile);

      if (!mobile || !isAuthenticated) {
        setIsVisible(false);
        return;
      }

      // Check if dismissed recently
      const dismissedAt = localStorage.getItem(DISMISS_KEY);
      if (dismissedAt) {
        const dismissedTime = parseInt(dismissedAt, 10);
        if (Date.now() - dismissedTime < DISMISS_DURATION_MS) {
          setIsVisible(false);
          return;
        }
        // Clear expired dismissal
        localStorage.removeItem(DISMISS_KEY);
      }

      setIsVisible(true);
    };

    checkVisibility();

    // Re-check on resize (user might rotate device)
    window.addEventListener('resize', checkVisibility);
    return () => window.removeEventListener('resize', checkVisibility);
  }, [isAuthenticated]);

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    setIsVisible(false);
  };

  const handleOpenApp = async () => {
    const token = await getAccessToken();
    if (token) {
      openAppWithAuth(token, null);
    } else {
      // No auth, just open app store
      window.location.href = getAppStoreUrl();
    }
  };

  if (!isVisible || !isMobile) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 safe-area-inset-bottom">
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white px-4 py-3 shadow-lg">
        <div className="flex items-center justify-between gap-3 max-w-lg mx-auto">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="flex-shrink-0 w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Smartphone className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <p className="font-medium text-sm">{t('openInAppBanner.title', 'Get the RUMIO App')}</p>
              <p className="text-xs text-primary-100 truncate">{t('openInAppBanner.subtitle', 'Continue where you left off')}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleOpenApp}
              className="px-4 py-2 bg-white text-primary-700 text-sm font-medium rounded-lg hover:bg-primary-50 transition-colors"
            >
              {t('openInAppBanner.open', 'Open')}
            </button>
            <button
              onClick={handleDismiss}
              className="p-2 text-primary-200 hover:text-white transition-colors"
              aria-label={t('openInAppBanner.dismiss', 'Dismiss')}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
